import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Upload, CheckCircle, X, FileText, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TransferEditDialog({ open, onOpenChange, transfer, formData, setFormData, proofPreview, setProofPreview, uploading, onProofUpload, onSubmit }) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin_transfers_edit_title')}</DialogTitle>
          <DialogDescription>{t('admin_transfers_transfer_id')}: {transfer?.id.substring(0, 16)}...</DialogDescription>
        </DialogHeader>
        {transfer && (
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div className="text-sm text-muted-foreground">
              {t('amount')}: <span className="font-bold">¥{transfer.total_jpy.toLocaleString()}</span> → <span className="font-bold text-accent">R$ {transfer.amount_brl.toLocaleString()}</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin_transfers_status_label')}</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="edit-status-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('status_pending')}</SelectItem>
                    <SelectItem value="processing">{t('status_processing')}</SelectItem>
                    <SelectItem value="completed">{t('status_completed')}</SelectItem>
                    <SelectItem value="failed">{t('status_failed')}</SelectItem>
                    <SelectItem value="cancelled">{t('status_cancelled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin_transfers_payment_label')}</Label>
                <Select value={formData.payment_status} onValueChange={(v) => setFormData({ ...formData, payment_status: v })}>
                  <SelectTrigger data-testid="edit-payment-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="awaiting">{t('status_pending')}</SelectItem>
                    <SelectItem value="paid">{t('status_completed')}</SelectItem>
                    <SelectItem value="confirmed">{t('admin_deposits_status_confirmed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.status === 'completed' && (
              <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-4 space-y-4" data-testid="completion-details">
                <div className="text-sm font-semibold text-emerald-700">{t('admin_transfers_completion_info')}</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin_transfers_settlement_id')}</Label>
                    <Input value={formData.settlement_id} onChange={(e) => setFormData({ ...formData, settlement_id: e.target.value })} placeholder={t('admin_transfers_settlement_placeholder')} data-testid="settlement-id-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin_transfers_completed_date')} <span className="text-red-500">*{t('admin_transfers_required')}</span></Label>
                    <Input type="date" value={formData.completed_at} onChange={(e) => setFormData({ ...formData, completed_at: e.target.value })} required data-testid="completed-at-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t('admin_transfers_settled_amount')} (BRL)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                    <Input type="number" value={formData.settled_amount} onChange={(e) => setFormData({ ...formData, settled_amount: e.target.value })} placeholder={transfer.amount_brl?.toString() || '0'} className="pl-10" step="0.01" data-testid="settled-amount-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t('admin_transfers_proof')} (PDF)</Label>
                  {proofPreview ? (
                    <div className="flex items-center gap-3 p-2.5 border border-border rounded-lg bg-secondary/20" data-testid="proof-preview">
                      <div className="w-8 h-8 bg-red-500/10 rounded flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{typeof proofPreview === 'string' && proofPreview.startsWith('http') ? t('admin_transfers_pdf_uploaded') : proofPreview}</div>
                        <div className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle className="h-2.5 w-2.5" />{t('admin_transfers_pdf_uploaded')}</div>
                      </div>
                      {formData.proof_image && (
                        <a href={`${API_URL}${formData.proof_image}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      <button type="button" onClick={() => { setProofPreview(null); setFormData(prev => ({ ...prev, proof_image: '' })); }} className="text-muted-foreground hover:text-red-600 flex-shrink-0">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-border rounded-lg p-2.5 text-center hover:border-accent transition-colors">
                      <input id="proof-upload" type="file" accept=".pdf,application/pdf" onChange={onProofUpload} className="hidden" data-testid="proof-upload-input" />
                      <label htmlFor="proof-upload" className="cursor-pointer flex items-center justify-center gap-2">
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{uploading ? t('uploading') : t('admin_transfers_proof') + ' PDF'}</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Button className="w-full" onClick={onSubmit} data-testid="update-transfer-button">{t('update')}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
